import { auth } from '../firebase';

import { Capacitor } from '@capacitor/core';

const BASE = Capacitor.isNativePlatform() ? 'https://airscribe.app/api' : '/api';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (auth.currentUser) {
    headers['Authorization'] = `Bearer ${await auth.currentUser.getIdToken()}`;
  } else {
    const guestToken = localStorage.getItem('airscribe_guest_token');
    if (guestToken) headers['x-guest-token'] = guestToken;
  }

  const res = await fetch(`${BASE}${path}`, { headers, ...options });

  if (res.status === 401) {
    if (auth.currentUser) {
      await auth.signOut();
    } else {
      localStorage.removeItem('airscribe_guest_token');
    }
    throw new Error('Session expired. Please sign in again.');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export const api = {
  // Patients
  getPatients: (search?: string) =>
    request<any[]>(`/patients${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  getPatient: (id: number) => request<any>(`/patients/${id}`),
  createPatient: (data: any) => request<any>('/patients', { method: 'POST', body: JSON.stringify(data) }),
  updatePatient: (id: number, data: any) => request<any>(`/patients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePatient: (id: number) => request<void>(`/patients/${id}`, { method: 'DELETE' }),

  // Encounters
  getEncounters: (patientId: number) => request<any[]>(`/encounters/patient/${patientId}`),
  getTodayEncounter: (patientId: number) => request<any>(`/encounters/today/${patientId}`),
  createEncounter: (data: any) => request<any>('/encounters', { method: 'POST', body: JSON.stringify(data) }),
  updateEncounter: (id: number, data: any) => request<any>(`/encounters/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteEncounter: (id: number) => request<void>(`/encounters/${id}`, { method: 'DELETE' }),

  // Encounter notes
  addNote: (encounterId: number, content: string) =>
    request<any>(`/encounters/${encounterId}/notes`, { method: 'POST', body: JSON.stringify({ content }) }),
  deleteNote: (encounterId: number, noteId: number) =>
    request<void>(`/encounters/${encounterId}/notes/${noteId}`, { method: 'DELETE' }),
  generateSoap: (encounterId: number) =>
    request<any>(`/encounters/${encounterId}/generate-soap`, { method: 'POST' }),

  // Presets
  getPresets: () => request<any[]>('/presets'),
  createPreset: (data: any) => request<any>('/presets', { method: 'POST', body: JSON.stringify(data) }),
  updatePreset: (id: number, data: any) => request<any>(`/presets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePreset: (id: number) => request<void>(`/presets/${id}`, { method: 'DELETE' }),

  // Recordings
  getRecordings: () => request<any[]>('/recordings'),
  getPatientRecordings: (patientId: number) => request<any[]>(`/recordings/patient/${patientId}`),
  createRecording: (data: { transcript: string }) => request<any>('/recordings', { method: 'POST', body: JSON.stringify(data) }),
  assignRecording: (id: number, patient_id: number | null) => request<any>(`/recordings/${id}/assign`, { method: 'PATCH', body: JSON.stringify({ patient_id }) }),
  deleteRecording: (id: number) => request<void>(`/recordings/${id}`, { method: 'DELETE' }),

  // AI
  structureSoap: (rawNotes: string) =>
    request<{ subjective: string; objective: string; assessment: string; plan: string }>(
      '/ai/structure-soap', { method: 'POST', body: JSON.stringify({ rawNotes }) }
    ),
  suggestBilling: (soap: { subjective: string; objective: string; assessment: string; plan: string }) =>
    request<Array<{ type: string; code: string; description: string; rationale: string }>>(
      '/ai/suggest-billing', { method: 'POST', body: JSON.stringify(soap) }
    ),

  transcribe: (audioBase64: string) =>
    request<{ text: string }>(
      '/ai/transcribe', { method: 'POST', body: JSON.stringify({ audio: audioBase64 }) }
    ),

  getTranscribeToken: () =>
    request<{ token: string }>('/ai/transcribe-token', { method: 'POST' }),

  getMe: () => request<{ id: number; email: string }>('/me'),

  getEncounter: (id: number) => request<any>(`/encounters/${id}`),

  // Guest auth
  createGuestAccount: () =>
    fetch(`${BASE}/auth/guest`, { method: 'POST', headers: { 'Content-Type': 'application/json' } }).then(r => r.json()) as Promise<{ guestToken: string; userId: number }>,
  linkGuestAccount: (guestToken: string) =>
    request<{ status: string }>('/auth/link', { method: 'POST', body: JSON.stringify({ guestToken }) }),
};
